import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { decodeJwtToken } from '../../utils';
import { LoginResponse, Session } from '../../types/RoleType';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authstore';
import { useAcademicYearStore } from '../../store/academicYearStore';
import { ApiResponse } from '../../types/common';
import { getCsrfToken } from '../../utils/csrf';
import { trackEvent } from '../../lib/mixpanel';
import { logger } from '../../lib/logger';
import { ApiValidationError, SystemError } from '../../lib/errors';
import { generateReferenceId } from '../../store/errorStore';
import { isStaging, isProduction, EnvironmentConfig } from '../../lib/environment';

/** Generates a UUID used as X-Request-Id on every outgoing request. */
function generateRequestId(): string {
  return crypto.randomUUID();
}

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Just-in-time refresh threshold used by the request interceptor — the last-resort
 * safety net for the window between useSessionManager's background polls (every 60s,
 * see PROACTIVE_REFRESH_THRESHOLD there). Keep this <= the polling interval so the
 * interceptor only ever fires for requests that land between two polls, not as the
 * primary refresh mechanism.
 */
export const TOKEN_REFRESH_THRESHOLD = 60000; // 1 minute

const CONFIG = {
  MAX_RETRIES: EnvironmentConfig.getMaxRetries(),
  RETRY_DELAY: 1000,
  REQUEST_TIMEOUT: EnvironmentConfig.getRequestTimeout(),
  TOKEN_REFRESH_THRESHOLD,
  RETRY_STATUS_CODES: new Set([408, 429, 500, 502, 503, 504])
} as const;

// Types
interface RetryableRequest extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _requestId?: string;
  _referenceId?: string;
}

interface RefreshApiResponse {
  succeeded: boolean;
  messages: string[];
  data: {
    newAccessToken: string;
    newRefreshToken: string;
    refreshTokenExpiryTime: string;
    userImageURL?: string | null;
    errorCode?: number | null;
  };
}

// Create a class to manage the refresh token state
class TokenRefreshManager {
  private refreshPromise: Promise<string> | null = null;
  private refreshing: boolean = false;

  async refreshToken(
    session: Session | null,
    updateSession: (session: Session) => void,
    logout: () => void,
    silent: boolean = false
  ): Promise<string> {

    if (this.refreshing) {
      return this.refreshPromise ?? Promise.reject(new Error('Refresh promise not found'));
    }

    try {
      this.refreshing = true;
      this.refreshPromise = this.doRefreshToken(session, updateSession, logout, silent);
      return await this.refreshPromise;

    } finally {
      this.refreshing = false;
      this.refreshPromise = null;
    }
  }

  private async doRefreshToken(
    session: Session | null,
    updateSession: (session: Session) => void,
    logout: () => void,
    silent: boolean = false
  ): Promise<string> {
    if (!session?.refreshToken) {
      throw new Error('No refresh token available');
    }

    // Check refresh token expiration before making the request
    if (Date.now() >= session.refreshTokenExpiresAt) {
      logout();
      throw new Error('Refresh token has expired');
    }

    try {
      if (!silent) toast.loading('Refreshing session...', { id: 'refresh-token' });
      const response = await axios.post<RefreshApiResponse>(`${API_URL}/auth/refresh`, {
        token: session.accessToken,
        refreshToken: session.refreshToken,
      });

      if (!response.data.succeeded) {
        const errorCode = response.data.data?.errorCode;
        // Only force-logout when the refresh token itself is expired or invalid.
        // For other failures (user not found, account inactive) also redirect to login.
        // Transient failures don't set an errorCode — avoid logging the user out.
        const shouldLogout = errorCode != null;
        if (shouldLogout) {
          if (!silent) toast.error('Session expired. Please log in again.', { id: 'refresh-token' });
          logout();
        } else {
          if (!silent) toast.error('Session refresh failed', { id: 'refresh-token' });
        }
        throw new Error(`Session refresh unsuccessful. ${response.data.messages[0]}`);
      }

      const { newAccessToken, newRefreshToken, refreshTokenExpiryTime, userImageURL } = response.data.data;
      const decodedToken = decodeJwtToken(newAccessToken);
      if (!decodedToken) {
        throw new Error('Invalid token received during refresh');
      }
      const newSession = {
        ...session,
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
        expiresAt: decodedToken.exp * 1000,
        refreshTokenExpiresAt: new Date(refreshTokenExpiryTime).getTime(),
        user: {
          ...session.user,
          profileImageUrl: userImageURL || session.user.profileImageUrl,
          // Re-read isSubscribed from the fresh JWT so it stays accurate after payment
          isSubscribed: decodedToken.isSubscribed === 'true',
        },
      };

      updateSession(newSession);
      if (!silent) toast.success('Session refreshed', { id: 'refresh-token' });
      return newAccessToken;
    } catch (error) {
      // Only force-logout when the server explicitly rejects the refresh token
      // (401/403 = token invalid or revoked). For transient network/server errors
      // the user's refresh token is still valid — let the next request retry.
      const isHardAuthFailure =
        error instanceof AxiosError &&
        [401, 403].includes(error.response?.status ?? 0);

      if (isHardAuthFailure) {
        if (!silent) toast.error('Session expired. Please log in again.', { id: 'refresh-token' });
        logout();
      } else if (!silent) {
        toast.error('Could not refresh session. Please check your connection.', { id: 'refresh-token' });
      }
      throw error;
    }
  }
}


// Utility functions
const sleep = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

const IDEMPOTENT_METHODS = new Set(['get', 'head', 'options', 'put', 'delete']);

const isRetryableError = (error: unknown): error is AxiosError => {
  if (!(error instanceof AxiosError)) return false;
  if (!CONFIG.RETRY_STATUS_CODES.has(error.response?.status || 0)) return false;
  // Never retry non-idempotent methods (POST, PATCH) — duplicate writes are worse than a failed request.
  const method = error.config?.method?.toLowerCase() ?? '';
  return IDEMPOTENT_METHODS.has(method);
};

async function retryRequest<T>(
  fn: () => Promise<T>,
  retries: number = 0
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (retries < CONFIG.MAX_RETRIES && isRetryableError(error)) {
      await sleep(CONFIG.RETRY_DELAY * Math.pow(2, retries));
      return retryRequest(fn, retries + 1);
    }
    throw error;
  }
}

export const tokenManager = new TokenRefreshManager();

export function createAxiosInstance(
  getSession: () => Session | null,
  updateSession: (session: Session) => void,
  logout: () => void
): AxiosInstance {
  const axiosInstance = axios.create({
    baseURL: API_URL,
    timeout: 60000,
    validateStatus: (status) => status >= 200 && status < 300,
  });

  // Request interceptor
  axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const session = getSession();

      // Update last activity timestamp
      useAuthStore.getState().updateLastActivity();

      if (!session?.accessToken) {
        return config;
      }

      // Auto-inject instituteId header for multi-tenant isolation
      if (session.user?.instituteId) {
        config.headers['X-Institute-Id'] = String(session.user.instituteId);
      }

      // Inject selected academic year for historical data views
      const { selectedAcademicYearId, defaultAcademicYearId } = useAcademicYearStore.getState();
      if (selectedAcademicYearId && selectedAcademicYearId !== defaultAcademicYearId) {
        config.headers['X-Academic-Year-Id'] = String(selectedAcademicYearId);
      }

      // Check if token needs refresh
      if (session.expiresAt &&
        Date.now() >= session.expiresAt - CONFIG.TOKEN_REFRESH_THRESHOLD) {
        const newToken = await tokenManager.refreshToken(
          session,
          updateSession,
          logout
        );
        config.headers.Authorization = `Bearer ${newToken}`;
      } else {
        config.headers.Authorization = `Bearer ${session.accessToken}`;
      }

      // Add CSRF token to state-changing requests (if available)
      // CSRF token will be available once backend implements CSRF protection
      const csrfToken = getCsrfToken();
      if (csrfToken && ['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase() || '')) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }

      const requestId = generateRequestId();
      const referenceId = generateReferenceId();

      config.headers['X-Request-Id'] = requestId;
      config.headers['X-Reference-Id'] = referenceId;

      // Store on config so the response error interceptor can retrieve them
      // without re-reading headers (headers may be normalised/missing by then).
      (config as RetryableRequest)._requestId = requestId;
      (config as RetryableRequest)._referenceId = referenceId;

      return config;
    },
    (error: AxiosError) => Promise.reject(error)
  );

  // Response interceptor
  axiosInstance.interceptors.response.use(
    (response) => {
      // ✅ handle ApiResponse<T> success/failure
      const apiResponse = response.data as ApiResponse<unknown>;
      if (apiResponse && apiResponse.succeeded === false) {
        const messages = apiResponse.messages?.length
          ? apiResponse.messages
          : ['Request failed'];
        logger.warn('API returned success=false', {
          url: response.config?.url || 'unknown',
          method: response.config?.method?.toUpperCase() || 'UNKNOWN',
          messages,
        });
        // Throw a typed error so the error pipeline can display the backend
        // messages verbatim instead of mapping them to a generic fallback.
        throw new ApiValidationError(messages);
      }
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as RetryableRequest;
      const url = originalRequest?.url || 'unknown';
      const method = originalRequest?.method?.toUpperCase() || 'UNKNOWN';
      const status = error.response?.status;

      // ✅ Handle token expiry (401)
      if (error.response?.status === 401 && !originalRequest._retry) {
        try {
          originalRequest._retry = true;
          const newToken = await tokenManager.refreshToken(
            getSession(),
            updateSession,
            logout
          );
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return retryRequest(() => axios(originalRequest));
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }

      // ✅ Handle 400 (Bad Request) — backend may include validation messages
      if (error.response?.status === 400) {
        const data = error.response.data as Partial<ApiResponse<unknown>>;
        if (data?.messages?.length) {
          logger.warn('API 400 Bad Request', { url, method, messages: data.messages });
          return Promise.reject(new ApiValidationError(data.messages));
        }
        const rawMessage = 'Bad request. Please check input values.';
        logger.warn('API 400 Bad Request', { url, method, rawMessage });
        return Promise.reject(new Error(rawMessage));
      }

      // ✅ Log all other API errors with full context for observability
      const rawMessage = (error as Error).message || 'Unexpected error';

      // Read both IDs from the config object (set by the request interceptor).
      const requestId = (originalRequest as RetryableRequest)?._requestId ?? undefined;
      const referenceId = (originalRequest as RetryableRequest)?._referenceId ?? undefined;

      const responseData = error.response?.data as Record<string, unknown> | undefined;
      const backendTraceId = (responseData?.traceId as string) ?? undefined;

      logger.error('API request failed', error as Error, {
        url,
        method,
        status: String(status ?? 'unknown'),
        rawMessage,
        ...(requestId ? { requestId } : {}),
        ...(referenceId ? { referenceId } : {}),
        ...(backendTraceId ? { backendTraceId } : {}),
      });

      return Promise.reject(new SystemError(rawMessage, { requestId, referenceId, backendTraceId, statusCode: status }));
    }
  );

  // ✅ Mixpanel CRUD Tracking Interceptor
  // Deferred via queueMicrotask so the response/rejection reaches the caller
  // immediately — analytics classification never sits on the request's critical path.
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      queueMicrotask(() => trackCRUDOperation(response));
      return response;
    },
    (error: AxiosError) => {
      if (error.config && error.response) {
        const errorResponse = error.response;
        queueMicrotask(() => trackCRUDOperation(errorResponse, true));
      }
      return Promise.reject(error);
    }
  );

  return axiosInstance;
}

/**
 * Helper function to track CRUD operations based on HTTP method and URL
 */
function trackCRUDOperation(response: AxiosResponse, isError: boolean = false) {
  try {
    const method = response.config?.method?.toUpperCase();
    const url = response.config?.url || '';
    const status = response.status;

    // Skip tracking for auth endpoints (already tracked separately)
    if (url.includes('/auth/')) return;

    // Extract entity type from URL (e.g., '/api/students' -> 'Student')
    const entityType = getEntityTypeFromUrl(url);
    if (!entityType) return;

    // Map HTTP method to CRUD action
    const actionMap: Record<string, string> = {
      POST: 'Created',
      GET: 'Viewed',
      PUT: 'Updated',
      PATCH: 'Updated',
      DELETE: 'Deleted',
    };

    const action = method && actionMap[method];
    if (!action) return;

    const eventName = `${entityType} ${action}`;

    // Track the event
    trackEvent(eventName, {
      entity_type: entityType.toLowerCase(),
      method,
      status,
      success: !isError && status >= 200 && status < 300,
      endpoint: url,
    });
  } catch (error) {
    // Silently fail to avoid breaking the request
    logger.warn('Error tracking CRUD operation', { errorMessage: error instanceof Error ? error.message : String(error) });
  }
}

/**
 * Extract entity type from URL path
 * Examples:
 *  - /api/students -> Student
 *  - /api/employees -> Employee
 *  - /api/classes -> Class
 */
function getEntityTypeFromUrl(url: string): string | null {
  // Common API patterns
  const patterns = [
    { regex: /\/students/i, entity: 'Student' },
    { regex: /\/employees/i, entity: 'Employee' },
    { regex: /\/classes/i, entity: 'Class' },
    { regex: /\/subjects/i, entity: 'Subject' },
    { regex: /\/exams/i, entity: 'Exam' },
    { regex: /\/schedules/i, entity: 'Schedule' },
    { regex: /\/marks/i, entity: 'Marks' },
    { regex: /\/attendance/i, entity: 'Attendance' },
    { regex: /\/fees/i, entity: 'Fee' },
    { regex: /\/fee-structure/i, entity: 'FeeStructure' },
    { regex: /\/salary/i, entity: 'Salary' },
    { regex: /\/leaves/i, entity: 'Leave' },
    { regex: /\/holidays/i, entity: 'Holiday' },
    { regex: /\/events/i, entity: 'Event' },
    { regex: /\/timetable/i, entity: 'Timetable' },
    { regex: /\/transport/i, entity: 'Transport' },
    { regex: /\/vehicles/i, entity: 'Vehicle' },
    { regex: /\/routes/i, entity: 'Route' },
    { regex: /\/hostels/i, entity: 'Hostel' },
    { regex: /\/rooms/i, entity: 'Room' },
    { regex: /\/grading/i, entity: 'GradingSystem' },
    { regex: /\/users/i, entity: 'User' },
    { regex: /\/institute/i, entity: 'Institute' },
    { regex: /\/academicyears/i, entity: 'AcademicYear' },
  ];

  for (const { regex, entity } of patterns) {
    if (regex.test(url)) {
      return entity;
    }
  }

  return null;
}

// Create the axios instance with your auth store 
export const axiosInstance = createAxiosInstance(
  () => useAuthStore.getState().session,
  (newSession) => useAuthStore.getState().updateSession(newSession),
  () => useAuthStore.getState().logout()
);